import React, { useState, useEffect, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils';

const INTRO_STEPS = [
  {
    id: 'renhed',
    num: '01',
    keyword: 'Renhed',
    quote: 'Det reneste udtryk for den nordiske natur.',
    body: 'Vi destillerer ikke for at masseproducere, men for at indfange naturens rå renhed i hver eneste flaske.',
    tags: ['100% Sydals', 'Ufiltreret kildevand', 'Håndplukket'],
    visualImage: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/billede-1.jpg',
    videoSrc: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/haeld-op-bred.mp4'
  },
  {
    id: 'taalmodighed',
    num: '02',
    keyword: 'Tålmodighed',
    quote: 'Langsom kobberdestillation uden genveje.',
    body: 'Hver batch skabes i hånden på Sydals med nøje udvalgte botanicals, krystalklart vand og tid.',
    tags: ['Kobberdestillation', 'Små batches', 'Tid & tålmodighed'],
    visualImage: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/img-4202.jpg',
    videoSrc: null
  },
  {
    id: 'karakter',
    num: '03',
    keyword: 'Karakter',
    quote: 'Kompromisløs smag og nordisk sjæl.',
    body: 'Fra prisbelønnet fadlagret gin til vilde slåenlikører – skabt med dyb respekt for råvarens karakter.',
    tags: ['Fadlagring', 'Vilde botanicals', 'Guldvinder'],
    visualImage: 'https://assets.auxodev.dk/assets/c8fc1da7-d576-4041-8666-592c544bae24/dsc02185.jpg',
    videoSrc: null
  }
];

export default function Intro() {
  const [steps, setSteps] = useState(INTRO_STEPS);
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    async function fetchIntroSteps() {
      try {
        const res = await fetch('/_api/public/v2/content/intro_trin?sort=sortering&order=asc');
        if (res.ok) {
          const json = await res.json();
          if (json?.items && json.items.length > 0) {
            const mapped = json.items.map((item, idx) => ({
              id: item.id || `step-${idx}`,
              num: item.nummer || `0${idx + 1}`,
              keyword: item.noegleord || '',
              quote: item.citat || '',
              body: item.beskrivelse || '',
              tags: typeof item.tags === 'string'
                ? item.tags.split(',').map((s) => s.trim()).filter(Boolean)
                : (Array.isArray(item.tags) ? item.tags : []),
              visualImage: Array.isArray(item.billede) ? item.billede[0] : item.billede,
              videoSrc: item.video_url || null,
            }));
            setSteps(mapped);
          }
        }
      } catch (err) {
        console.warn('Using fallback intro steps:', err);
      }
    }
    fetchIntroSteps();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const totalScrollable = Math.max(1, rect.height - window.innerHeight);
      const scrolled = Math.min(totalScrollable, Math.max(0, -rect.top));
      const progress = Math.min(1, Math.max(0, scrolled / totalScrollable));
      
      setScrollProgress(progress);

      const totalChapters = steps.length;
      const stepIndex = Math.min(
        totalChapters - 1,
        Math.floor(progress * totalChapters)
      );
      setActiveStep(stepIndex);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [steps.length]);

  const currentItem = steps[activeStep] || steps[0];

  const scrollToStep = (index) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    const sectionTop = window.scrollY + rect.top;
    const totalScrollable = Math.max(1, rect.height - window.innerHeight);
    const targetProgress = index / (steps.length - 1);
    const targetScrollY = sectionTop + targetProgress * totalScrollable;
    window.scrollTo({
      top: targetScrollY,
      behavior: 'smooth'
    });
  };

  return (
    <section
      id="intro"
      ref={sectionRef}
      className="relative w-full bg-[#F6F2EB] min-h-[300vh] text-[#37332F]"
      aria-label="Introduktion til KALAS"
    >
      {/* Sticky Fullscreen Presentation Frame */}
      <div className="sticky top-0 w-full h-screen overflow-hidden flex flex-col justify-between pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-8 md:pb-12 px-6 sm:px-10 md:px-16 bg-[#F6F2EB]">
        
        {/* Living Visual Background: dynamic image/video crossfade with warm paper toning */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          {steps.map((step, idx) => {
            const isCurrent = activeStep === idx;
            return (
              <div
                key={step.id}
                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
                  isCurrent ? 'opacity-35 scale-100' : 'opacity-0 scale-105'
                }`}
                style={{ transitionProperty: 'opacity, transform' }}
              >
                {step.videoSrc ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full h-full object-cover brightness-[0.92] contrast-[0.95] sepia-[0.08]"
                  >
                    <source src={step.videoSrc} type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src={getOptimizedImageUrl(step.visualImage, 1920, 85)}
                    alt={step.keyword}
                    className="w-full h-full object-cover brightness-[0.92] contrast-[0.95] sepia-[0.08]"
                  />
                )}
              </div>
            );
          })}

          {/* Warm paper gradient & vignette overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#F6F2EB]/95 via-[#F6F2EB]/82 to-[#F6F2EB]/95 pointer-events-none" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(246,242,235,0.75)_20%,_rgba(246,242,235,0.95)_100%)] pointer-events-none" />
        </div>

        {/* Top Header: Step Indicators */}
        <div className="relative z-20 w-full max-w-7xl mx-auto flex items-center justify-between border-b border-[#DED7CC]/80 pb-4">
          <span className="font-['Montserrat'] text-[11px] uppercase tracking-[0.25em] text-[#8E867E]">
            Destillatørens Filosofi
          </span>
          {/* Step Line Indicators */}
          <div className="flex items-center gap-2">
            {steps.map((s, idx) => (
              <button
                key={s.id}
                type="button"
                onClick={() => scrollToStep(idx)}
                aria-label={`Gå til kapitel ${s.num}: ${s.keyword}`}
                className="group py-2 px-1 cursor-pointer focus:outline-none"
              >
                <span
                  className={`block h-[2px] transition-all duration-500 ${
                    activeStep === idx
                      ? 'w-8 bg-[#C99850]'
                      : 'w-3 bg-[#DED7CC] group-hover:bg-[#C99850]/60'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Center: Graphic Typography & Shifting Short Text */}
        <div className="relative z-20 w-full max-w-7xl mx-auto my-auto py-4 sm:py-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left: Graphic Keyword & Number with Smooth Crossfade */}
            <div className="lg:col-span-5 relative grid grid-cols-1 grid-rows-1">
              {steps.map((step, idx) => {
                const isCurrent = activeStep === idx;
                return (
                  <div
                    key={step.id}
                    className={`col-start-1 row-start-1 transition-all ease-out ${
                      isCurrent
                        ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto z-10 duration-300 delay-75'
                        : 'opacity-0 -translate-y-2 scale-98 pointer-events-none z-0 duration-150'
                    }`}
                  >
                    {/* Oversized Graphic Watermark Number */}
                    <span className="font-['Cormorant_Garamond'] text-7xl sm:text-8xl md:text-9xl font-light text-[#37332F]/5 absolute -top-10 -left-4 sm:-left-8 select-none pointer-events-none">
                      {step.num}
                    </span>

                    <div className="relative space-y-2.5">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-[1px] bg-[#C99850]" />
                        <span className="font-['Montserrat'] text-xs uppercase tracking-[0.25em] text-[#C99850]">
                          Kapitel {step.num}
                        </span>
                      </div>
                      <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl md:text-5xl lg:text-[2.6rem] xl:text-[3.25rem] font-light uppercase tracking-[0.06em] text-[#2C2825] leading-tight break-normal">
                        {step.keyword}
                      </h2>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: Editorial quote & text */}
            <div className="lg:col-span-7 lg:border-l lg:border-[#DED7CC]/80 lg:pl-10 xl:pl-14 relative grid grid-cols-1 grid-rows-1">
              {steps.map((step, idx) => {
                const isCurrent = activeStep === idx;
                return (
                  <div
                    key={step.id}
                    className={`col-start-1 row-start-1 space-y-4 transition-all ease-out ${
                      isCurrent
                        ? 'opacity-100 translate-y-0 pointer-events-auto z-10 duration-300 delay-75'
                        : 'opacity-0 -translate-y-2 pointer-events-none z-0 duration-150'
                    }`}
                  >
                    <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl md:text-3xl text-[#2C2825] font-normal italic leading-snug">
                      "{step.quote}"
                    </p>
                    <p className="text-sm sm:text-base text-[#5E5852] font-light leading-relaxed max-w-xl">
                      {step.body}
                    </p>

                    {/* Minimal Graphic Tags */}
                    <div className="pt-2 flex flex-wrap gap-2 text-[10px] font-['Montserrat'] uppercase tracking-[0.16em] text-[#5E5852]">
                      {step.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-[#EAE5DD]/80 border border-[#DED7CC] text-[#4A453F] transition-colors"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>

        {/* Bottom Bar: Smooth Progress Line & Next Cue */}
        <div className="relative z-20 w-full max-w-7xl mx-auto flex items-center justify-between pt-4 border-t border-[#DED7CC] text-xs font-['Montserrat'] text-[#8E867E]">
          <div className="flex items-center gap-3">
            <span className="tracking-widest uppercase text-[10px]">Scroll for at udforske</span>
            <span className="hidden sm:inline text-[#DED7CC]">·</span>
            <span className="hidden sm:inline text-[10px] text-[#8E867E]/80">
              Kapitel {currentItem.num} af {String(steps.length).padStart(2, '0')}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {activeStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => scrollToStep(activeStep + 1)}
                className="text-[11px] uppercase tracking-wider text-[#C99850] hover:text-[#B08038] cursor-pointer transition-colors flex items-center gap-1.5 focus:outline-none"
              >
                <span>Næste kapitel</span>
                <ArrowDown className="w-3 h-3" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const storyEl = document.getElementById('story');
                  if (storyEl) storyEl.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-[11px] uppercase tracking-wider text-[#C99850] hover:text-[#B08038] cursor-pointer transition-colors flex items-center gap-1.5 focus:outline-none"
              >
                <span>Fortsæt til film</span>
                <ArrowDown className="w-3 h-3" aria-hidden="true" />
              </button>
            )}
            <span className="text-[#C99850] font-medium tracking-wider min-w-[36px] text-right">
              {Math.round(scrollProgress * 100)}%
            </span>
          </div>
        </div>

        {/* Bottom smooth edge gradient transition */}
        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-[#F6F2EB] to-transparent pointer-events-none z-10" />
      </div>
    </section>
  );
}
